<script setup lang="ts">
import { computed } from 'vue'

import { useInsightConfigPipeline } from '../../../composables/useInsightConfigPipeline'
import type { InsightConfigMeta } from '../../../models/insightConfig.types'
import type { PipelineStepEditor } from '../../../service/insightConfigPipeline'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { JsonField } from '@/shared/ui/json-field'
import { Select, type SelectOption } from '@/shared/ui/select'

const editors = defineModel<PipelineStepEditor[]>({ required: true })
const props = defineProps<{
  meta: InsightConfigMeta
  warning?: boolean
  errors: Record<string, string[]>
  disabled?: boolean
}>()
const { t } = useI18n()
const pipeline = useInsightConfigPipeline(editors, () => props.meta)
const stepOptions = computed<SelectOption<string>[]>(() =>
  props.meta.pipeline_step_types.map((value) => ({ value, label: value })),
)
const transformOptions = computed<SelectOption<string>[]>(() =>
  props.meta.supported_process_transforms.map((value) => ({ value, label: value })),
)

function errorFor(index: number): string {
  const messages =
    props.errors[`pipeline_steps[${index + 1}]`] ?? props.errors['pipeline_steps'] ?? []
  return messages
    .map((message) => (message.startsWith('insights.') ? t(message) : message))
    .join(' ')
}
</script>

<template>
  <div class="pipeline-builder grid gap-sm">
    <Alert v-if="warning" tone="warning">
      {{ t('insights.sysadmin.config.llmPipelineWarning') }}
    </Alert>
    <article
      v-for="(editor, index) in editors"
      :key="editor.id"
      class="pipeline-builder__step grid gap-sm p-md border border-border rounded-md bg-surface-sunken"
    >
      <header>
        <strong>{{ t('insights.sysadmin.config.pipelineStep', { count: index + 1 }) }}</strong>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          :disabled="disabled"
          @click="pipeline.removeStep(index)"
        >
          {{ t('insights.sysadmin.config.removeStep') }}
        </Button>
      </header>
      <Select
        :model-value="editor.type"
        :label="t('insights.sysadmin.config.fields.pipelineStepType')"
        :options="stepOptions"
        :disabled="disabled"
        @update:model-value="$event !== null && pipeline.updateType(index, String($event))"
      />
      <JsonField
        :model-value="editor.configText"
        :spec="{ kind: 'object', nullable: false, defaultValue: {} }"
        :label="t('insights.sysadmin.config.fields.pipelineStepConfig')"
        :error-message="errorFor(index)"
        :default-label="t('insights.sysadmin.config.useDefault')"
        :disabled="disabled"
        @update:model-value="pipeline.updateConfig(index, $event)"
      />
      <Select
        v-if="editor.type === 'process' && transformOptions.length"
        :model-value="null"
        :label="t('insights.sysadmin.config.fields.processTransform')"
        :placeholder="t('insights.sysadmin.config.addTransform')"
        :options="transformOptions"
        :disabled="disabled"
        @update:model-value="$event !== null && pipeline.addTransform(index, String($event))"
      />
    </article>
    <div class="pipeline-builder__actions flex items-center justify-between gap-sm">
      <Button
        type="button"
        size="sm"
        variant="outline"
        :disabled="disabled || !pipeline.canAdd.value"
        @click="pipeline.addStep"
      >
        {{ t('insights.sysadmin.config.addStep') }}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        :disabled="disabled"
        @click="pipeline.resetDefaults"
      >
        {{ t('insights.sysadmin.config.resetPipeline') }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.pipeline-builder .pipeline-builder__step header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-sm);
}
</style>
